/**
 * RCC routes — sub-routes under /rcc/*.
 *
 * Dashboard at /rcc. Each of the 14 surfaces at /rcc/<slug>.
 */

import type { ComponentType } from 'react';
import {
  ShieldAlert,
  FileText,
  DollarSign,
  ClipboardList,
  Mail,
  UserX,
  Clock,
  BarChart3,
  Heart,
  CalendarDays,
  BadgeCheck,
  AlertTriangle,
  Timer,
  CloudRainWind,
  HeartPulse,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

import { OfacTriage } from './surfaces/OfacTriage';
import { SarDraft } from './surfaces/SarDraft';
import { CtrWorksheet } from './surfaces/CtrWorksheet';
import { CipExceptions } from './surfaces/CipExceptions';
import { AdverseAction } from './surfaces/AdverseAction';
import { DeathNotice } from './surfaces/DeathNotice';
import { Escheatment } from './surfaces/Escheatment';
import { ComplianceReports } from './surfaces/ComplianceReports';
import { NoPodRelease } from './surfaces/NoPodRelease';
import { FsrUbWeekly } from './surfaces/FsrUbWeekly';
import { BsaAmlAnnual } from './surfaces/BsaAmlAnnual';
import { NewesiNewodNofundZrocls } from './surfaces/NewesiNewodNofundZrocls';
import { DormantRelease } from './surfaces/DormantRelease';
import { CtrDisaster } from './surfaces/CtrDisaster';
import { HealthNetRelease } from './surfaces/HealthNetRelease';
import { AdverseActionQueue } from '@/modules/Compliance/AdverseActionQueue';
import { Inbox } from 'lucide-react';

export interface RccSurfaceDef {
  slug: string;
  number: number;
  title: string;
  blurb: string;
  icon: LucideIcon;
  Component: ComponentType;
  category: 'live' | 'workflow' | 'periodic' | 'reference';
}

export const RCC_SURFACES: RccSurfaceDef[] = [
  {
    slug: 'ofac-triage',
    number: 1,
    title: 'OFAC Triage',
    blurb: 'Review OFAC hits; clear false positives; escalate true matches.',
    icon: ShieldAlert,
    Component: OfacTriage,
    category: 'live',
  },
  {
    slug: 'sar-draft',
    number: 2,
    title: 'SAR Drafting',
    blurb: 'Draft Suspicious Activity Report narrative for Dataverse.',
    icon: FileText,
    Component: SarDraft,
    category: 'workflow',
  },
  {
    slug: 'ctr-worksheet',
    number: 3,
    title: 'CTR Worksheet',
    blurb: 'Currency Transaction Report worksheet — FinCEN $10k threshold.',
    icon: DollarSign,
    Component: CtrWorksheet,
    category: 'workflow',
  },
  {
    slug: 'cip-exceptions',
    number: 4,
    title: 'CIP Exceptions',
    blurb: 'Customer Identification Program exception queue.',
    icon: ClipboardList,
    Component: CipExceptions,
    category: 'live',
  },
  {
    slug: 'adverse-action',
    number: 5,
    title: 'Adverse Action',
    blurb: 'Reg B 30-day clock; notice dispatch via RNC physical mail.',
    icon: Mail,
    Component: AdverseAction,
    category: 'workflow',
  },
  {
    // Wave 6.2 — cart-resolution-code Adverse-Action queue.
    slug: 'adverse-action-queue',
    number: 16,
    title: 'Adverse-Action queue',
    blurb: 'Pending review backlog from carts closed with the Adverse-Action resolution code.',
    icon: Inbox,
    Component: AdverseActionQueue,
    category: 'live',
  },
  {
    slug: 'death-notice',
    number: 6,
    title: 'Death Notice',
    blurb: 'Deceased customer workflow — POD, estate, SDB referral.',
    icon: UserX,
    Component: DeathNotice,
    category: 'workflow',
  },
  {
    slug: 'escheatment',
    number: 7,
    title: 'Escheatment',
    blurb: 'MO RSMo 447.539.9 dormancy + escheatment rules.',
    icon: Clock,
    Component: Escheatment,
    category: 'reference',
  },
  {
    slug: 'reports',
    number: 8,
    title: 'Compliance Reports',
    blurb: 'Scheduled + ad-hoc reports — runs delegated to RAS.',
    icon: BarChart3,
    Component: ComplianceReports,
    category: 'periodic',
  },
  {
    slug: 'no-pod-release',
    number: 10,
    title: 'No-POD Release',
    blurb: 'Estate documentation + Release and Hold Harmless workflow.',
    icon: Heart,
    Component: NoPodRelease,
    category: 'workflow',
  },
  {
    slug: 'fsr-ub-weekly',
    number: 11,
    title: 'FSR-UB Weekly',
    blurb: 'Undeliverable / Unclaimed Balance weekly review.',
    icon: CalendarDays,
    Component: FsrUbWeekly,
    category: 'periodic',
  },
  {
    slug: 'bsa-aml-annual',
    number: 12,
    title: 'BSA/AML Annual',
    blurb: 'Annual BSA/AML certification record.',
    icon: BadgeCheck,
    Component: BsaAmlAnnual,
    category: 'periodic',
  },
  {
    slug: 'weekly-exceptions',
    number: 13,
    title: 'Weekly Exceptions',
    blurb: 'NEWESI / NEWOD / NOFUND / ZROCLS weekly scans.',
    icon: AlertTriangle,
    Component: NewesiNewodNofundZrocls,
    category: 'periodic',
  },
  {
    slug: 'dormant-release',
    number: 14,
    title: 'Dormant Release',
    blurb: 'Release dormant flag when customer re-engages.',
    icon: Timer,
    Component: DormantRelease,
    category: 'workflow',
  },
  {
    slug: 'ctr-disaster',
    number: 15,
    title: 'CTR Disaster',
    blurb: 'CTR aggregation during FEMA/state disaster declarations.',
    icon: CloudRainWind,
    Component: CtrDisaster,
    category: 'reference',
  },
  {
    // Wave 2 Death Notice rebuild — locked 2026-04-27 decision #4.
    slug: 'healthnet-release',
    number: 17,
    title: 'HealthNet Release',
    blurb: 'MO HealthNet (Medicaid) decedent release-of-funds. Owned by Kent.',
    icon: HeartPulse,
    Component: HealthNetRelease,
    category: 'workflow',
  },
];
